import React, { useEffect, useRef, useState } from "react";
import { Ruler, ChevronDown } from "lucide-react";
import { LengthUnit, TempUnit, VelocityUnit, useUnits } from "../utils/units";

/*
 * The units control, in the header so it reaches every page.
 *
 * Three independent choices rather than one metric/imperial switch: shooters
 * here read velocity in fps, temperature in Celsius and group size in inches or
 * MOA, which no single switch produces. Each is remembered separately.
 */

function Row<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-muted">{label}</span>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      <div className="mt-1.5 flex gap-1">
        {options.map((o) => (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`flex-1 rounded-md px-2 py-1 text-[13px] font-bold transition-colors ${
              value === o ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {o === "C" ? "°C" : o === "F" ? "°F" : o}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function UnitSwitcher({ dark = false }: { dark?: boolean }) {
  const { units, setUnit } = useUnits();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-away, so the panel behaves like every other menu on the page.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Display units"
        className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-semibold transition-colors ${
          dark
            ? "border-white/20 text-white/90 hover:bg-white/10"
            : "border-line bg-white text-ink hover:bg-slate-50"
        }`}
      >
        <Ruler size={15} />
        <span className="hidden sm:inline">
          {units.velocity} · °{units.temp} · {units.length}
        </span>
        <ChevronDown size={13} />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1.5 w-60 rounded-xl border border-line bg-white py-1 shadow-modal">
          <div className="px-3 pb-1 pt-2 text-[13px] font-bold text-ink">Display units</div>
          <Row<VelocityUnit>
            label="Velocity"
            value={units.velocity}
            options={["fps", "m/s"] as const}
            onChange={(v) => setUnit("velocity", v)}
          />
          <Row<TempUnit> label="Temperature" value={units.temp} options={["C", "F"] as const} onChange={(v) => setUnit("temp", v)} />
          <Row<LengthUnit>
            label="Measurements"
            hint="on the target"
            value={units.length}
            options={["MOA", "inch", "mm"] as const}
            onChange={(v) => setUnit("length", v)}
          />
          <p className="px-3 pb-2 pt-1 text-[11px] leading-snug text-muted">
            Distances stay as the shoot recorded them — yards or metres.
          </p>
        </div>
      )}
    </div>
  );
}
